<template>
  <div>
    <v-container class="px-2 pt-2 pb-8" :style="'height: '+ (wHeight-112) + 'px'">

      <!-- ═══════ HEADER & INTRODUCTION ═══════ -->
      <div class="mb-2 text-center text-sm-left">
        <h1 class="text-h4 font-weight-black mb-2">{{ $t(page+'.Help Page') }}</h1>
        <p class="text-body-1 grey--text text--darken-1 mb-0">
          {{ $t(page+'.Manage your personal details view your booking history or request account anonymization') }}
        </p>
      </div>

      <v-stepper v-model="helpStep" flat class="elevation-0 px-2">
        <v-stepper-items>

          <!-- ═══════ STEP 1: Landing — either "manage my account" or the recovery chooser ═══════ -->
          <v-stepper-content step="1" class="pa-0">

            <!-- ── Signed-in state: quick account management ── -->
            <template v-if="isLoggedIn && !showSwitchAccount">
              <!-- PROFILE FORM -->
              <v-card outlined class="pa-4 mb-4 app-card">
                <div class="text-subtitle-1 font-weight-bold mb-3">
                  <v-icon left>mdi-account-circle-outline</v-icon>
                  {{ $t(page+'.Your Profile Details') || 'Your Profile Details' }}
                </div>
                
                <v-form ref="profileForm" v-model="profileValid">
                  <v-row dense>
                    <v-col cols="6" sm="4">
                      <v-text-field
                        v-model="customer.firstName"
                        :label="$t(page+'.First Name') || 'First Name'"
                        outlined dense hide-details
                        :rules="[v => !!v || 'First name is required']"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6" sm="4">
                      <v-text-field
                        v-model="customer.lastName"
                        :label="$t(page+'.Last Name')"
                        outlined dense hide-details
                        :rules="[v => !!v || 'Last name is required']"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="customer.email"
                        :label="$t(page+'.Email') || 'Email'"
                        outlined dense hide-details
                        type="email"
                        :rules="[v => !v || /.+@.+\..+/.test(v) || 'E-mail must be valid']"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="8" md="4">
                      <div class="readonly-phone-wrap" @click="dialog_readonly_phone = true">
                        <v-text-field
                          :value="customer.phone ? '+' + (customer.countryCode || '39') + ' ' + customer.phone : ''"
                          :label="$t(page+'.Phone Number')"
                          outlined dense hide-details
                          disabled
                          prepend-inner-icon="mdi-lock-outline"
                          messages="Phone details are locked to your login profile"
                        ></v-text-field>
                      </div>
                    </v-col>                    
                    <v-col cols="4" md="4">
                      <v-text-field
                        v-model="customer.postalCode"
                        :label="$t(page+'.Postal Code')"
                        outlined dense hide-details
                        type="number"
                      ></v-text-field>
                    </v-col> 
                    <v-col cols="6" md="4">
                      <v-menu
                        ref="menuBirthdate"
                        v-model="menuBirthdate"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="customer.birthdate"
                            :label="$t(page+'.Birthdate')"
                            prepend-inner-icon="mdi-calendar"
                            outlined 
                            readonly
                            hide-details
                            dense
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          ref="birthdate"
                          no-title
                          first-day-of-week=1
                          :locale="locale"
                          v-model="customer.birthdate"
                          :color="settings.options.color"
                          :max="subtractYears(13)"
                          :min="subtractYears(99)"
                          @change="saveBirthdate"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" md="6" class="d-flex align-center">
                      <v-switch
                        v-model="customer.newsletterOptIn"
                        :label="$t(page+'.Subscribe to Newsletter') || 'Subscribe to Newsletter'"
                        hide-details
                        dense
                        inset
                        class="mt-0"
                      ></v-switch>
                    </v-col>                    

                    <v-col cols="6" md="6">
                      <div class="d-flex justify-end mt-2">
                        <v-btn 
                          color="black" 
                          dark 
                          outlined
                          class="app-btn mt-n1" 
                          :disabled="!profileValid" 
                          :loading="helpLoading" 
                          @click="updateProfile()"
                        >
                          <v-icon left small>mdi-content-save-outline</v-icon>
                          {{ $t(page+'.Save')}}
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card>

              <v-row>
                <v-col class="py-1">
                  <div class="help-tile">
                    <v-icon class="mr-3" color="error">mdi-delete-forever-outline</v-icon>
                    <div class="flex-grow-1">
                      <div class="text-body-2 font-weight-bold">{{ $t(page+'.Delete Account') }}</div>
                      <div class="text-caption grey--text text--darken-1 mb-2">{{ $t(page+'.Want to wipe out Cookies and Settings? Click here!') }}</div>
                      <v-btn small outlined color="" class="app-btn" @click="dialog_wipeOut = true">
                        <v-icon left small>mdi-delete-forever-outline</v-icon>
                        {{ $t(page+'.Wipe Out') || 'Wipe Out' }}
                      </v-btn>
                    </div>
                  </div>
                </v-col>
                <v-col class="py-1">
                  <div class="help-tile">
                    <v-icon class="mr-3" color="error">mdi-incognito</v-icon>
                    <div class="flex-grow-1">
                      <div class="text-body-2 font-weight-bold">{{ $t(page+'.Anonymization') }}</div>
                      <div class="text-caption grey--text text--darken-1 mb-2">{{ $t(page+'.Anonymize your Name, Surname and phone with just one click') }}</div>
                      <v-btn small outlined color="" class="app-btn" @click="openAnonymize()">
                        <v-icon left small>mdi-incognito</v-icon>
                        {{ $t(page+'.Anonymize Me') || 'Anonymize Me' }}
                      </v-btn>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <!-- RESERVATION HISTORY -->
              <v-card outlined class="mt-4 pa-4 mb-4 app-card" v-if="localHistory.length || historyLoading">
                <div class="text-subtitle-1 font-weight-bold mb-3">
                  <v-icon left>mdi-history</v-icon>
                  {{ $t(page+'.Your Reservation History') || 'Your Reservation History' }}
                </div>
                <v-data-table
                  :headers="historyHeaders"
                  :items="localHistory"
					        :loading="historyLoading"					   
                  dense
                  :item-class="getRowClass"
                  :items-per-page="5"
                  class="app-table"
                >
                  <template v-slot:[`item.covers`]="{ item }">
                    {{ item.covers }} {{ item.children? ' + ' + item.children:'' }}
                  </template>
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-tooltip top v-if="!item.anonymized">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small v-bind="attrs" v-on="on" @click="openAnonymizeReservation(item)">
                          <v-icon small color="error">mdi-incognito</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t(page+'.Anonymize this reservation') || 'Anonymize this reservation' }}</span>
                    </v-tooltip>
                    <v-chip v-else x-small outlined color="grey">
                      {{ $t(page+'.Anonymized') || 'Anonymized' }}
                    </v-chip>
                  </template>
                  <template v-slot:[`item.score`]="{ item }">
                    <div class="d-flex">
                        <v-icon
                          v-for="n in 5"
                          :key="n"
                          :color="n <= item.score ? 'amber' : 'grey lighten-1'"
                        >
                          mdi-star
                        </v-icon>
                      </div>                    
                  </template>                  
                </v-data-table>
              </v-card>

              <v-btn block outlined color="black" class="app-btn mt-5" @click="showSwitchAccount = true">
                <v-icon left small>mdi-account-switch-outline</v-icon>
                {{ $t(page+'.Recover Account') }}
              </v-btn>


            </template>

            <!-- ── No session (or user asked to switch account): guided recovery chooser ── -->
            <template v-else>
              <v-card outlined class="pa-4 mb-4 app-card">  
                <div class="mb-4">
                  <p class="text-subtitle-1 font-weight-bold mb-1">{{ $t(page+'.Lets get you back in') || "Let's get you back in" }}</p>
                  <p class="text-subtitle-1 grey--text text--darken-1 mb-0">{{ $t(page+'.We could not find an active session on this device Pick how you would like to verify it is you') || "We couldn't find an active session on this device — pick how you'd like to verify it's you." }}</p>
                </div>

                <!-- Method 1: OTP via WhatsApp — fastest & most secure, recommended -->
                <div class="method-tile mb-3" v-ripple @click="openOtpRelink()">
                  <div class="method-icon-badge method-icon-badge--green">
                    <v-icon color="white" size="20">mdi-whatsapp</v-icon>
                  </div>
                  <div class="flex-grow-1">
                    <div class="d-flex align-center">
                      <span class="text-body-2 font-weight-bold">{{ $t(page+'.Verify with WhatsApp code') || 'Verify with WhatsApp code' }}</span>
                    </div>
                    <div class="text-caption grey--text text--darken-1">{{ $t(page+'.We will send via WhatsApp a one time code to your number') }}</div>
                  </div>
                  <v-icon color="grey darken-1">mdi-chevron-right</v-icon>
                </div>

                <!-- Method 2: Password set at booking time -->
                <div class="method-tile mb-3" v-ripple @click="openPasswordRecovery()">
                  <div class="method-icon-badge method-icon-badge--black">
                    <v-icon color="white" size="20">mdi-key-outline</v-icon>
                  </div>
                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-bold">{{ $t(page+'.Sign in with password') }}</div>
                    <div class="text-caption grey--text text--darken-1">{{ $t(page+'.Use the password you set when booking') }}</div>
                  </div>
                  <v-icon color="grey darken-1">mdi-chevron-right</v-icon>
                </div>

                <!-- Method 3: Find by reservation details -->
                <div class="method-tile mb-2" v-ripple @click="openReservationRecovery()">
                  <div class="method-icon-badge method-icon-badge--black">
                    <v-icon color="white" size="20">mdi-calendar-search</v-icon>
                  </div>
                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-bold">{{ $t(page+'.Find my upcoming reservation') || 'Find my upcoming reservation' }}</div>
                    <div class="text-caption grey--text text--darken-1">{{ $t(page+'.No password No problem Match by phone date time and party size') || "No password? No problem — match by phone, date, time & party size." }}</div>
                  </div>
                  <v-icon color="grey darken-1">mdi-chevron-right</v-icon>
                </div>
                <div class="mt-4 text-center">
                  <v-btn block v-if="isLoggedIn" outlined color="black" class="app-btn mb-3" @click="showSwitchAccount = false">
                    <v-icon left small>mdi-chevron-left</v-icon>{{ $t(page+'.Back to my account') || 'Back to my account' }}
                  </v-btn>
                </div>
              </v-card>              
            </template>
          </v-stepper-content>

          <!-- ═══════ STEP 2: Anonymize ═══════ -->
          <v-stepper-content step="2" class="pa-1">
            <div class="d-flex align-center mb-4">
              <v-btn icon small @click="helpStep = 1"><v-icon>mdi-chevron-left</v-icon></v-btn>
              <span class="text-subtitle-1 font-weight-bold ml-1">{{ $t(page+'.Anonymization') || 'Anonymization' }}</span>
            </div>
            <v-alert type="warning" dense outlined class="text-body-2 mb-3">
              {{ $t(page+'.This will permanently remove your personal data name phone email from our records Your booking history will be anonymized and cannot be recovered') || 'This will permanently remove your personal data (name, phone, email) from our records. Your booking history will be anonymized and cannot be recovered.' }}
            </v-alert>
            <v-text-field
              v-model="helpConfirmText"
              :label="$t(page+'.Type DELETE to confirm') || 'Type DELETE to confirm'"
              outlined dense
              hide-details
              class="mb-3"
            ></v-text-field>
            <v-btn
              block color="error" dark class="app-btn"
              :disabled="helpConfirmText !== 'DELETE'"
              :loading="helpLoading"
              @click="doAnonymize()"
            >
              <v-icon small left>mdi-incognito</v-icon>
              {{ $t(page+'.Anonymize My Data') || 'Anonymize My Data' }}
            </v-btn>
          </v-stepper-content>

          <!-- ═══════ STEP 3: Re-link account by phone + OTP verify ═══════ -->
          <v-stepper-content step="3" class="pa-1">
            <div class="d-flex align-center mb-4">
              <v-btn icon small @click="backToChooser()"><v-icon>mdi-chevron-left</v-icon></v-btn>
              <span class="text-subtitle-1 font-weight-bold ml-1">{{ $t(page+'.WhatsApp verification') || 'WhatsApp verification' }}</span>
            </div>

            <template v-if="!helpOtpSent">
              <p class="text-body-2 mb-3">{{ $t(page+'.If you changed device or cleared your data enter your phone number to re-link your account') || 'If you changed device or cleared your data, enter your phone number to re-link your account.' }}</p>
              <v-select
                color="black"
                dense
                outlined
                v-model="helpCountryCode"
                :items="countries"
                :label="$t(page+'.Country Code')"
                hide-details
                item-value="value"
                :item-text="item => item.namel+' +'+item.value"
                class="mb-3"
              >
                <template v-slot:item="slotProps">
                  <i :class="['mr-2','em',slotProps.item.flag]"></i>
                  {{slotProps.item.namel}} + {{slotProps.item.value}}
                </template>
                <template v-slot:selection="data">
                  <i :class="['mr-2','em',data.item.flag]"></i>
                  +{{data.item.value}}
                </template>
              </v-select>
              <v-text-field
                v-model="helpPhone"
                :label="$t(page+'.WhatsApp number') || 'WhatsApp number'"
                outlined dense
                append-icon="mdi-whatsapp"
                hide-details
                reverse
                class="mb-3"
                type="tel"
                v-mask="'### ### ## ##'"
              ></v-text-field>
              <v-btn
                block color="black" dark class="app-btn"
                :disabled="!helpPhone || helpPhone.replace(/\s/g,'').length < 9"
                :loading="helpLoading"
                @click="doRelink()"
              >
                <v-icon small left>mdi-whatsapp</v-icon>
                {{ $t(page+'.Get OTP') || 'Get OTP' }}
              </v-btn>
            </template>

            <template v-else>
              <v-alert type="success" dense outlined class="text-body-2 mb-3">
                {{ $t(page+'.OTP sent to') || 'OTP sent to' }} <strong>{{ helpPhone }}</strong>
              </v-alert>
              <v-text-field
                v-model="helpOtp"
                :label="$t(page+'.Enter OTP code 9 digits') || 'Enter OTP code (9 digits)'"
                outlined dense
                append-icon="mdi-numeric"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                placeholder="000-000-000"
                v-mask="'###-###-###'"
                class="mb-3"
                reverse
                :maxlength="11"
                :minlength="11"
                :loading="helpLoading"
                :hint="helpOtpError"
                :error="!!helpOtpError"
                persistent-hint
                @input="onOtpInput"
              ></v-text-field>

              <div class="mt-4 text-center">
              <v-btn outlined small color="grey darken-1" class="app-btn" @click="backToChooser()">
                {{ $t(page+'.Try a different method') }}
              </v-btn>
            </div>
            </template>
          </v-stepper-content>

          <!-- ═══════ STEP 4: Success ═══════ -->
          <v-stepper-content step="4" class="pa-1 text-center">
            <v-icon color="success" style="font-size:72px" class="mt-4">mdi-check-circle-outline</v-icon>
            <p class="text-h6 mt-3">{{ helpSuccessMsg }}</p>
            <v-btn outlined small color="black" class="app-btn mt-4" @click="finishAndReturn()">
              {{ $t(page+'.Close') || 'Close' }}
            </v-btn>
          </v-stepper-content>

          <!-- ═══════ STEP 5: Failed (OTP relink failure only — other flows show inline errors instead) ═══════ -->
          <v-stepper-content step="5" class="pa-1 text-center">
            <v-icon color="error" style="font-size:72px" class="mt-4">mdi-close-circle-outline</v-icon>
            <p class="text-h6 mt-3">{{ helpOtpError }}</p>
            <v-btn outlined small color="black" class="app-btn mt-4" @click="helpStep = 3; helpOtpSent = false">
              {{ $t(page+'.Try Again') || 'Try Again' }}
            </v-btn>
            <div class="mt-4 text-center">
              <v-btn outlined small color="grey darken-1" class="app-btn" @click="backToChooser()">
                {{ $t(page+'.Try a different method') }}
              </v-btn>
            </div>
          </v-stepper-content>

          <!-- ═══════ STEP 6: Recover by reservation details (phone + day + time + covers) ═══════ -->
          <v-stepper-content step="6" class="pa-1">
            <div class="d-flex align-center mb-4">
              <v-btn icon small @click="backToChooser()"><v-icon>mdi-chevron-left</v-icon></v-btn>
              <span class="text-subtitle-1 font-weight-bold ml-1">{{ $t(page+'.Find my reservation') || 'Find my reservation' }}</span>
            </div>

            <p class="text-body-2 mb-3">{{ $t(page+'.Enter the details of an upcoming reservation to find your account') || 'Enter the details of an upcoming reservation to find your account.' }}</p>
            <v-row>
              <v-col>
                <v-select
                  color="black"
                  dense
                  outlined
                  v-model="helpCountryCode"
                  :items="countries"
                  :label="$t(page+'.Country Code')"
                  hide-details
                  item-value="value"
                  :item-text="item => item.namel+' +'+item.value"
                  class="mb-3"
                >
                  <template v-slot:item="slotProps">
                    <i :class="['mr-2','em',slotProps.item.flag]"></i>
                    {{slotProps.item.namel}} + {{slotProps.item.value}}
                  </template>
                  <template v-slot:selection="data">
                    <i :class="['mr-2','em',data.item.flag]"></i>
                    +{{data.item.value}}
                  </template>
                </v-select>
              </v-col>
              <v-col>
            <v-text-field
              v-model="helpPhone"
              :label="$t(page+'.WhatsApp number') || 'WhatsApp number'"
              outlined dense
              append-icon="mdi-whatsapp"
              hide-details
              reverse
              class="mb-3"
              type="tel"
              v-mask="'### ### ## ##'"
            ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-menu
                  v-model="recDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="recDate"
                      outlined dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :label="$t(page+'.Reservation date') || 'Reservation date'"
                      append-icon="mdi-calendar"
                      hide-details
                      class="mb-3"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="recDate"
                    :min="todayISO"
                    no-title
                    @input="recDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col>
                <v-menu
                  v-model="recTimeMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  left
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="recTime"
                      outlined dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :label="$t(page+'.Reservation time') || 'Reservation time'"
                      append-icon="mdi-clock-outline"
                      hide-details
                      format="24hr"
                      class="mb-3"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-if="recTimeMenu"
                    v-model="recTime"
                    format="24hr"
                    :allowed-minutes="m => m % 15 === 0"
                    @input="recTimeMenu = false"
                  ></v-time-picker>
                </v-menu>
              </v-col>
            </v-row>
            
            <v-select
              v-model="recCovers"
              :items="coverOptions"
              outlined dense
              reverse
              :label="$t(page+'.Number of covers')"
              hide-details
              class="mb-3"
            ></v-select>

            <v-alert v-if="recLookupError" type="error" dense outlined class="text-body-2 mb-3">
              {{ recLookupError }}
            </v-alert>

            <v-btn
              block color="black" dark class="app-btn mb-2"
              :disabled="!recFormValid"
              :loading="helpLoading"
              @click="doRecoverByReservation()"
            >
              <v-icon small left>mdi-calendar-search</v-icon>
              {{ $t(page+'.Find My Reservation') || 'Find My Reservation' }}
            </v-btn>

            <div class="mt-4 text-center">
              <v-btn outlined small color="grey darken-1" class="app-btn" @click="backToChooser()">
                {{ $t(page+'.Try a different method') }}
              </v-btn>
            </div>
          </v-stepper-content>

          <!-- ═══════ STEP 7: Recover by password ═══════ -->
          <v-stepper-content step="7" class="pa-1">
            <div class="d-flex align-center mb-4">
              <v-btn icon small @click="backToChooser()"><v-icon>mdi-chevron-left</v-icon></v-btn>
              <span class="text-subtitle-1 font-weight-bold ml-1">{{ $t(page+'.Sign in with password') }}</span>
            </div>

            <p class="text-body-2 mb-3">{{ $t(page+'.Enter the phone number and password you set when booking')}}</p>
            <v-row>
              <v-col>
            <v-select
              color="black"
              dense
              outlined
              v-model="helpCountryCode"
              :items="countries"
              :label="$t(page+'.Country Code')"
              hide-details
              item-value="value"
              :item-text="item => item.namel+' +'+item.value"
              class="mb-3"
            >
              <template v-slot:item="slotProps">
                <i :class="['mr-2','em',slotProps.item.flag]"></i>
                {{slotProps.item.namel}} + {{slotProps.item.value}}
              </template>
              <template v-slot:selection="data">
                <i :class="['mr-2','em',data.item.flag]"></i>
                +{{data.item.value}}
              </template>
            </v-select>
              </v-col>
              <v-col>
            <v-text-field
              v-model="helpPhone"
              :label="$t(page+'.WhatsApp number') || 'WhatsApp number'"
              outlined dense
              append-icon="mdi-whatsapp"
              hide-details
              reverse
              class="mb-3"
              type="tel"
              v-mask="'### ### ## ##'"
            ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="recPassword"
                  :label="$t(page+'.Password')"
                  outlined dense
                  hide-details
                  class="mb-1"
                  :type="recPasswordVisible ? 'text' : 'password'"
                  :append-icon="recPasswordVisible ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="recPasswordVisible = !recPasswordVisible"
                  @keyup.enter="doRecoverByPassword()"
                ></v-text-field>
              </v-col>
            </v-row>

            <!--
            <div class="mb-3">
              <v-btn text x-small color="grey darken-1" class="app-btn pl-0" @click="openReservationRecovery()">
                {{ $t(page+'.Forgot your password Try finding your reservation instead') }}
              </v-btn>
            </div>
            -->

            <v-alert v-if="recPasswordError" type="error" dense outlined class="text-body-2 mb-3">
              {{ recPasswordError }}
            </v-alert>

            <v-btn
              block color="black" dark class="app-btn mb-2"
              :disabled="!passwordFormValid"
              :loading="helpLoading"
              @click="doRecoverByPassword()"
            >
              <v-icon small left>mdi-key-outline</v-icon>
              {{ $t(page+'.Sign In') || 'Sign In' }}
            </v-btn>

            <div class="mt-4 text-center">
              <v-btn outlined small color="grey darken-1" class="app-btn" @click="backToChooser()">
                {{ $t(page+'.Try a different method') }}
              </v-btn>
            </div>
          </v-stepper-content>

        </v-stepper-items>
      </v-stepper>
    </v-container>

    <!-- ─── WIPE OUT CONFIRMATION DIALOG ──────────────────────────── -->
    <v-dialog v-model="dialog_wipeOut" max-width="360" persistent>
      <v-card outlined class="app-dialog">
        <v-card-title class="app-dialog__title">
          <v-icon color="error" class="mr-2">mdi-delete-forever-outline</v-icon>
          <span class="text-subtitle-1 font-weight-bold">{{ $t(page+'.Wipe Out')}}</span>
        </v-card-title>
        <v-divider class="fw"></v-divider>
        <v-card-text class="text-body-2 pt-4">
          {{ $t(page+'.This will permanently delete your cookies and settings Are you sure')}}?
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn outlined color="black" class="app-btn mr-2" @click="dialog_wipeOut = false">{{ $t(page+'.Cancel') }}</v-btn>
          <v-btn color="error" dark class="app-btn" @click="confirmWipeOut()">{{ $t(page+'.Wipe Out')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ─── ANONYMIZE SINGLE RESERVATION CONFIRMATION DIALOG ────────── -->
    <v-dialog v-model="dialog_anonymizeReservation" max-width="360" persistent>
      <v-card outlined class="app-dialog">
        <v-card-title class="app-dialog__title">
          <v-icon color="error" class="mr-2">mdi-incognito</v-icon>
          <span class="text-subtitle-1 font-weight-bold">{{ $t(page+'.Anonymize Reservation') || 'Anonymize Reservation' }}</span>
        </v-card-title>
        <v-divider class="fw"></v-divider>
        <v-card-text class="text-body-2 pt-4">
          {{ $t(page+'.This will anonymize the personal data linked to this single reservation This cannot be undone') || 'This will anonymize the personal data linked to this single reservation. This cannot be undone.' }}
          <div class="mt-2" v-if="selectedHistoryItem">
            <strong>{{ selectedHistoryItem.date }} {{ selectedHistoryItem.time }}</strong> — {{ selectedHistoryItem.area }}
          </div>
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn outlined color="black" class="app-btn mr-2" @click="dialog_anonymizeReservation = false">{{ $t(page+'.Cancel') }}</v-btn>
          <v-btn color="error" dark class="app-btn" :loading="helpLoading" @click="confirmAnonymizeReservation()">{{ $t(page+'.Anonymize') || 'Anonymize' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: readonly phone/country tap -->
    <v-dialog v-model="dialog_readonly_phone" max-width="380">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          <v-icon left color="info">mdi-lock</v-icon>
          {{ $t(page+'.Phone number locked') }}
        </v-card-title>
        <v-card-text class="text-body-2 pt-2">
          {{ $t(page+'.Your phone number is linked to this device') }}.
          {{ $t(page+'.To use a different number, delete your account first, then re-register with the new number') }}.
        </v-card-text>
        <v-card-actions>
          <v-btn color="error" @click="dialog_readonly_phone = false; $emit('wipe-out')">
            <v-icon left small>mdi-delete-forever</v-icon>
            {{ $t(page+'.Delete User') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn @click="dialog_readonly_phone = false">{{ $t(page+'.OK') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>    

    <!-- ─── PROFILE / HISTORY FEEDBACK SNACKBAR ─────────────────────── -->
    <v-snackbar v-model="profileSnackbar" :color="profileSnackbarColor" timeout="3000">
      {{ profileSnackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  name: "HelpTab",
  props: {
    customerId: {
      type: String,
      default: null
    },
    customer: {
      type: Object,
      default: () => ({})
    },    
    isActive: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      page: "Booking",
      helpStep: 1,
      helpLoading: false,
      helpConfirmText: '',
      helpPhone: '',
      helpSuccessMsg: '',
      helpOtpSent: false,
      helpOtp: '',
      helpOtpError: '',
      helpOtpReady: false,
      helpCountryCode: 39,
      dialog_wipeOut: false,
      dialog_readonly_phone: false,
      showSwitchAccount: false,
      // Recovery-by-reservation fields
      recDate: '',
      recDateMenu: false,
      recTime: '',
      recTimeMenu: false,
      recCovers: 2,
      recLookupError: '',
      // Recovery-by-password fields
      recPassword: '',
      recPasswordVisible: false,
      recPasswordError: '',
      isLoggedIn: false,
      menuBirthdate: false,
      // Reservation history table
      localHistory: [],
		historyLoading: false,					
      dialog_anonymizeReservation: false,
      selectedHistoryItem: null,
      // Profile update / history feedback
      profileSnackbar: false,
      profileSnackbarText: '',
      profileSnackbarColor: 'success',
    }
  },
mounted() {
   this.isLoggedIn = this.customer.inci;
   console.log(562, 'isLoggedIn', this.customer,this.isLoggedIn)
	if (this.isLoggedIn) this.fetchCustomerHistory()											   
  },
  computed: {
    ...mapGetters(['settings', 'countries']),

    todayISO() {
      const d = new Date()
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    },
    coverOptions() {
      const arr = []
      for (let i = 1; i <= 12; i++) arr.push(i)
      return arr
    },
    recFormValid() {
      return !!this.helpPhone && this.helpPhone.replace(/\s/g, '').length >= 9 &&
        !!this.recDate && !!this.recTime && !!this.recCovers
    },
    passwordFormValid() {
      return !!this.helpPhone && this.helpPhone.replace(/\s/g, '').length >= 9 &&
        !!this.recPassword && this.recPassword.length >= 4
    },
    historyHeaders() {
      return [
        { text: this.$t(this.page+'.Date'), value: 'date' },
        { text: this.$t(this.page+'.Time'), value: 'time' },
        { text: this.$t(this.page+'.Area'), value: 'area' },
        { text: this.$t(this.page+'.Covers'), value: 'covers',align: 'center'  },
        { text: this.$t(this.page+'.Rating'), value: 'score' },
        //{ text: '', value: 'actions', sortable: false, align: 'end' },
      ]
    },
  },

  watch: {
    isActive: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // Reset to the landing step each time the tab is opened, so a half-finished
          // recovery attempt from a previous visit never lingers on screen.
          this.helpStep = 1
          this.showSwitchAccount = false
          this.recLookupError = ''
          this.recPasswordError = ''
          this.helpOtpError = ''
		if (this.isLoggedIn) this.fetchCustomerHistory()											  
        }
      }
    },
    menuBirthdate (val) {
      val && setTimeout(() => (this.$refs.birthdate.activePicker = 'YEAR'))
    },
    history: {
      immediate: true,
      handler(val) {
        this.localHistory = Array.isArray(val) ? val.map(h => ({ ...h })) : []
      }
    },
  },

  methods: {
    getRowClass(item) {
      // 1. Check if item exists first
      // 2. Safely check if active is false
      var r='';
      if (item && item.active === false) {
        r= 'row-cancelled';
      }
      console.log(825,item, r)
      return r;
    },    
    saveBirthdate (date) {
      this.$refs.menuBirthdate.save(date)
    },
    subtractYears(years) {
      var date = new Date(Date.now())
      date.setFullYear(date.getFullYear() - years);
      return date.toISOString().substring(0, 10);
    },
    // ─── Navigation helpers ──────────────────────────────────────
    openAnonymize() {
      this.helpConfirmText = ''
      this.helpStep = 2
    },
    openOtpRelink() {
      this.helpPhone = ''
      this.helpOtpSent = false
      this.helpOtp = ''
      this.helpOtpError = ''
      this.helpStep = 3
    },
    openReservationRecovery() {
      this.helpPhone = ''
      this.recDate = ''
      this.recTime = ''
      this.recCovers = 2
      this.recLookupError = ''
      this.helpStep = 6
    },
    openPasswordRecovery() {
      this.helpPhone = ''
      this.recPassword = ''
      this.recPasswordError = ''
      this.helpStep = 7
    },
    // Returns to the method chooser (step 1, rendered in its "chooser" branch)
    backToChooser() {
      this.helpStep = 1
      if (this.isLoggedIn) this.showSwitchAccount = true
    },
    finishAndReturn() {
      this.helpStep = 1
      this.showSwitchAccount = false
      this.$emit('change-tab', 'Info')
    },

    // ─── Wipe out ────────────────────────────────────────────────
    confirmWipeOut() {
      this.dialog_wipeOut = false
      localStorage.removeItem('customerId')
      location.reload()
    },

    // ─── Update profile ────────────────────────────────────────────
    // NOTE: backend action 'updateProfile' needs to be implemented server-side.
    // It should update firstName/lastName/email/postalCode/birthdate/optIn
    // (customers.optIn) for the given customerId and return the standard
    // errorid/payloads/message shape used by the other actions here.
    async updateProfile() {
      if (this.$refs.profileForm && !this.$refs.profileForm.validate()) return
      this.helpLoading = true
      try {
        const csrf = sessionStorage.getItem('csrf')
        const customerId = localStorage.getItem('customerId')
        const res = await this.$http.post('/booking/booking_stepper.php', {
          action: 'updateProfile',
          csrf,
          payload: {
            customerId,
            firstName: this.customer.firstName,
            lastName: this.customer.lastName,
            email: this.customer.email,
            postalCode: this.customer.postalCode,
            birthdate: this.customer.birthdate,
            newsletterOptIn: this.customer.newsletterOptIn?1:0,
          }
        })
        const newCsrf = res.headers['x-csrf-token']
        if (newCsrf) sessionStorage.setItem('csrf', newCsrf)

        /*
        if (res.data.errorid === '0') {
          this.profileSnackbarColor = 'success'
          this.profileSnackbarText = this.$t(this.page+'.Profile updated successfully') || 'Profile updated successfully.'
        } else {
          this.profileSnackbarColor = 'error'
          this.profileSnackbarText = res.data.message || 'An error occurred.'
        }
        */

        switch (res.data.errorid) {
          case '0':
            this.profileSnackbarColor = 'success'
            this.profileSnackbarText = this.$t(this.page+'.Profile updated successfully') 
            break
          case '0403':
            this.profileSnackbarColor = 'error'
            this.profileSnackbarText = res.data.message || 'An error occurred.'
            window.location.reload();
            break
          default:
            this.profileSnackbarColor = 'error'
            this.profileSnackbarText = res.data.message || 'An error occurred.'
        }

      } catch (e) {
        console.error('updateProfile error', e)
        this.profileSnackbarColor = 'error'
        this.profileSnackbarText = 'Connection error. Please try again.'
      } finally {
        this.helpLoading = false
        this.profileSnackbar = true
        window.location.reload();
      }
    },
    // ─── Reservation history: fetch (own ajax call, no full-page rebuild) ──
    // NOTE: backend action 'customerHistory_get' needs to be implemented
    // server-side. Input: { customerId }. Output: the standard
    // errorid/payloads shape, with payloads.history as an array of
    // { id, date, time, area, covers, children, anonymized }.
    async fetchCustomerHistory() {
      this.historyLoading = true
      try {
        const csrf = sessionStorage.getItem('csrf')
        const customerId = localStorage.getItem('customerId')
        const res = await this.$http.post('/booking/booking_stepper.php', {
          action: 'CustomerHistory_get',
          csrf,
          payload: { customerId }
        })
        const newCsrf = res.headers['x-csrf-token']
        if (newCsrf) sessionStorage.setItem('csrf', newCsrf)

        if (res.data.errorid === '0' && res.data.payloads && Array.isArray(res.data.payloads)) {
          this.localHistory = res.data.payloads.map(h => ({ ...h }))
        } else {
          this.localHistory = []
        }
      } catch (e) {
        console.error('fetchCustomerHistory error', e)
        this.localHistory = []
      } finally {
        this.historyLoading = false
      }
    },

																						
																		 
															   
																   
															  
								  
								
		   
												   
															 
																		   
										
			   
								 
		  
												   
															

																										
																			
				
								
		 
				   
													  
							  
				 
								   
	   
	  

    // ─── Reservation history: anonymize a single reservation ──────
    openAnonymizeReservation(item) {
      this.selectedHistoryItem = item
      this.dialog_anonymizeReservation = true
    },
    // NOTE: backend action 'anonymizeReservation' needs to be implemented
    // server-side. It should scrub the name/phone/email tied to that one
    // reservation record while leaving date/time/area/covers/children intact,
    // and only operate on a reservation that actually belongs to customerId.
    async confirmAnonymizeReservation() {
      if (!this.selectedHistoryItem) return
      this.helpLoading = true
      try {
        const csrf = sessionStorage.getItem('csrf')
        const customerId = localStorage.getItem('customerId')
        const res = await this.$http.post('/booking/booking_stepper.php', {
          action: 'anonymizeReservation',
          csrf,
          payload: {
            customerId,
            reservationId: this.selectedHistoryItem.id
          }
        })
        const newCsrf = res.headers['x-csrf-token']
        if (newCsrf) sessionStorage.setItem('csrf', newCsrf)

        if (res.data.errorid === '0') {
          const idx = this.localHistory.findIndex(h => h.id === this.selectedHistoryItem.id)
          if (idx !== -1) this.$set(this.localHistory[idx], 'anonymized', true)
          this.profileSnackbarColor = 'success'
          this.profileSnackbarText = this.$t(this.page+'.Reservation anonymized') || 'Reservation anonymized.'
        } else {
          this.profileSnackbarColor = 'error'
          this.profileSnackbarText = res.data.message || 'An error occurred.'
        }
      } catch (e) {
        console.error('confirmAnonymizeReservation error', e)
        this.profileSnackbarColor = 'error'
        this.profileSnackbarText = 'Connection error. Please try again.'
      } finally {
        this.helpLoading = false
        this.dialog_anonymizeReservation = false
        this.selectedHistoryItem = null
        this.profileSnackbar = true
      }
    },

    // ─── Anonymize ───────────────────────────────────────────────
    async doAnonymize() {
      this.helpLoading = true
      try {
        const csrf = sessionStorage.getItem('csrf')
        const customerId = localStorage.getItem('customerId')
        const res = await this.$http.post('/booking/booking_stepper.php', {
          action: 'optOut',
          csrf,
          payload: { customerId }
        })
        const newCsrf = res.headers['x-csrf-token']
        if (newCsrf) sessionStorage.setItem('csrf', newCsrf)

        if (res.data.errorid === '0') {
          localStorage.removeItem('customerId')
          sessionStorage.clear()
          this.helpSuccessMsg = this.$t(this.page+'.Your data has been anonymized') || 'Your data has been anonymized.'
          this.helpStep = 4
        } else {
          this.helpSuccessMsg = res.data.message || 'An error occurred.'
          this.helpStep = 4
        }
      } catch (e) {
        console.error('doAnonymize error', e)
        this.helpSuccessMsg = 'Connection error. Please try again.'
        this.helpStep = 4
      } finally {
        this.helpLoading = false
      }
    },

    // ─── OTP re-link (phone possession is the credential) ──
    async doRelink() {
      this.helpLoading = true
      try {
        const res = await this.$http.post('/booking/booking_stepper.php', {
          action: 'sendOtp',
          payload: {
            countryCode: this.helpCountryCode,
            phone: this.helpPhone.replace(/\s/g, ''),
          }
        })
        const newCsrf = res.headers['x-csrf-token']
        if (newCsrf) sessionStorage.setItem('csrf', newCsrf)

        if (res.data.errorid === '0') {
          this.helpOtpReady = false
          this.helpOtpSent = true
          this.helpOtp = ''
          this.helpOtpError = ''
          setTimeout(() => { this.helpOtp = ''; this.helpOtpReady = true }, 300)
        } else {
          this.helpOtpError = res.data.message || 'Failed to send OTP.'
          this.helpStep = 5
        }
      } catch (e) {
        console.error('doRelink error', e)
        this.helpOtpError = 'Connection error. Please try again.'
      } finally {
        this.helpLoading = false
      }
    },

    async onOtpInput(val) {
      if (!this.helpOtpReady) return
      const digits = val ? String(val).replace(/-/g, '') : ''
      if (digits.length < 9) {
        this.helpOtpError = ''
        return
      }
      this.helpLoading = true
      this.helpOtpError = ''
      try {
        const otp = digits
        const res = await this.$http.post('/booking/booking_stepper.php', {
          action: 'recoverAccount',
          payload: {
            phone: this.helpPhone.replace(/\s/g, ''),
            otp: otp,
            countryCode: this.helpCountryCode,
            customerId: localStorage.getItem('customerId')
          }
        })
        const newJwt = res.headers['authorization']
        if (newJwt) {
          this.$http.defaults.headers.common['Authorization'] = newJwt
          sessionStorage.setItem('jwt', newJwt)
        }
        const newCsrf = res.headers['x-csrf-token']
        if (newCsrf) sessionStorage.setItem('csrf', newCsrf)

        if (res.data.errorid === '0' && res.data.payloads && res.data.payloads.rc > 0) {
          const customerId = res.data.payloads.customerId
          if (customerId) localStorage.setItem('customerId', customerId)
          this.helpSuccessMsg = this.$t(this.page+'.Account re-linked successfully') 
          this.helpStep = 4
          setTimeout(() => { window.location.reload() }, 2500)
        } else {
          this.helpOtpError = res.data.message || 'Invalid OTP. Please try again.'
          this.helpStep = 5
        }
      } catch (e) {
        console.error('onOtpInput error', e)
        this.helpOtpError = 'Connection error. Please try again.'
      } finally {
        this.helpLoading = false
      }
    },

    // ─── Recover by reservation details (phone+day+time+covers) ──
    // NOTE: backend action 'recoverByReservation' needs to be implemented
    // server-side. It should:
    //   - only match FUTURE reservations (never past history)
    //   - require an exact match on phone + date + time + covers
    //   - apply rate limiting / lockout per phone and per IP
    //   - on a successful match, fire a passive WhatsApp notice to that
    //     phone ("your reservation was just accessed from a new device")
    //     as a non-blocking side effect, for audit/early-warning purposes
    async doRecoverByReservation() {
      this.helpLoading = true
      this.recLookupError = ''
      try {
        const res = await this.$http.post('/booking/booking_stepper.php', {
          action: 'recoverByReservation',
          payload: {
            countryCode: this.helpCountryCode,
            phone: this.helpPhone.replace(/\s/g, ''),
            date: this.recDate,
            time: this.recTime,
            covers: this.recCovers,
          }
        })
        const newJwt = res.headers['authorization']
        if (newJwt) {
          this.$http.defaults.headers.common['Authorization'] = newJwt
          sessionStorage.setItem('jwt', newJwt)
        }
        const newCsrf = res.headers['x-csrf-token']
        if (newCsrf) sessionStorage.setItem('csrf', newCsrf)

        if (res.data.errorid === '0' && res.data.payloads && res.data.payloads.customerId) {
          const customerId = res.data.payloads.customerId
          if (customerId) localStorage.setItem('customerId', customerId)
          this.helpSuccessMsg = this.$t(this.page+'.Reservation found Account linked') || 'Reservation found — your account is linked!'
          this.helpStep = 4
          setTimeout(() => { window.location.reload() }, 2500)
        } else {
          // Stay on the form so the user can immediately correct a typo,
          // rather than sending them through a dead-end error screen.
          this.recLookupError = res.data.message ||
            (this.$t(this.page+'.No matching upcoming reservation found Please check the details and try again') ||
            'No matching upcoming reservation found. Please check the details and try again.')
        }
      } catch (e) {
        console.error('doRecoverByReservation error', e)
        this.recLookupError = 'Connection error. Please try again.'
      } finally {
        this.helpLoading = false
      }
    },

    // ─── Recover by password (set optionally at first booking) ──
    // NOTE: backend action 'recoverByPassword' needs to be implemented
    // server-side. It should:
    //   - compare against the hashed password stored on customers/bookings
    //   - apply the same rate limiting / lockout discipline as any password
    //     login (this is now a real standalone credential, not a lookup aid)
    //   - return the same errorid/payloads/JWT shape as the other actions
    async doRecoverByPassword() {
      this.helpLoading = true
      this.recPasswordError = ''
      try {
        const res = await this.$http.post('/booking/booking_stepper.php', {
          action: 'recoverByPassword',
          payload: {
            countryCode: this.helpCountryCode,
            phone: this.helpPhone.replace(/\s/g, ''),
            password: this.recPassword,
          }
        })
        const newJwt = res.headers['authorization']
        if (newJwt) {
          this.$http.defaults.headers.common['Authorization'] = newJwt
          sessionStorage.setItem('jwt', newJwt)
        }
        const newCsrf = res.headers['x-csrf-token']
        if (newCsrf) sessionStorage.setItem('csrf', newCsrf)

        if (res.data.errorid === '0' && res.data.payloads && res.data.payloads.customerId) {
          const customerId = res.data.payloads.customerId
          if (customerId) localStorage.setItem('customerId', customerId)
          this.helpSuccessMsg = this.$t(this.page+'.Signed in successfully')
          this.helpStep = 4
          setTimeout(() => { window.location.reload() }, 2500)
        } else {
          // Stay on the form — same rationale as the reservation lookup above.
          this.recPasswordError = res.data.message ||
            (this.$t(this.page+'.Phone or password incorrect') || 'Phone or password incorrect.')
        }
      } catch (e) {
        console.error('doRecoverByPassword error', e)
        this.recPasswordError = 'Connection error. Please try again.'
      } finally {
        this.helpLoading = false
      }
    },
  }
}
</script>

<style scoped>
.app-dialog {
  border-radius: 16px !important;
  border-color: #e0e0e0 !important;
}

.app-dialog__title {
  display: flex;
  align-items: center;
  padding: 16px 16px 12px 16px !important;
}

.app-btn {
  border-radius: 10px !important;
}

.help-tile {
  display: flex;
  align-items: flex-start;
  border: 1.5px solid #e0e0e0;
  border-radius: 12px;
  padding: 12px;
}

.session-banner {
  display: flex;
  align-items: center;
  background: #f2faf4;
  border: 1.5px solid #cdeed7;
  border-radius: 12px;
  padding: 10px 14px;
}

/* Clickable method row — the page's one signature structural device:
   a decision list, since choosing a recovery method genuinely is a choice
   between three distinct paths, not a numbered sequence. */
.method-tile {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1.5px solid #e0e0e0;
  border-radius: 14px;
  padding: 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.method-tile:hover {
  border-color: #bdbdbd;
  background-color: #fafafa;
}

.method-icon-badge {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.method-icon-badge--black {
  background: #1a1a1a;
}

.method-icon-badge--green {
  background: #25a25a;
}

/* Captures taps on the disabled phone field so the lock dialog can open */
.readonly-phone-wrap {
  position: relative;
  cursor: pointer;
}

.readonly-phone-wrap::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 1;
}
</style>

<style scoped>
/* Use ::v-deep to penetrate the component scope */
::v-deep .row-cancelled {
  text-decoration: line-through;
  color: #a0a0a0; /* Optional: fades the text out a bit */
  background-color: #f9f9f9; /* Optional: slight background shift */
}
</style>